import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, ArrowLeft, Wifi, WifiOff, Heart, Tv } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { MoodIcon } from '../lib/moodIcons';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AuraCastPage() {
  const { auraKey } = useParams();
  const navigate = useNavigate();
  const [aura, setAura] = useState(null);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [newItem, setNewItem] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);

  // Initial fetch — load existing aura + media via REST
  const fetchInitial = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/auracast/${auraKey}`, { withCredentials: true });
      setAura(data.aura);
      setMedia(data.media || []);
    } catch (err) {
      setError('AuraCast not found');
    } finally {
      setLoading(false);
    }
  }, [auraKey]);

  // WebSocket connection with auto-reconnect
  const connectWebSocket = useCallback(() => {
    const wsBase = (API_URL || window.location.origin).replace(/^http/, 'ws');
    const ws = new WebSocket(`${wsBase}/api/ws/auracast/${auraKey}`);
    wsRef.current = ws;

    ws.onopen = () => {
      setIsConnected(true);
      // Heartbeat: send ping every 25s to keep connection alive
      const heartbeat = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send('ping');
      }, 25000);
      ws.addEventListener('close', () => clearInterval(heartbeat));
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.event === 'media_added') {
          setMedia(prev => {
            if (prev.find(m => m.id === msg.media.id)) return prev;
            return [msg.media, ...prev];
          });
          setNewItem(msg.media);
          setTimeout(() => setNewItem(null), 3000);
        } else if (msg.event === 'reaction') {
          setMedia(prev => prev.map(m =>
            m.id === msg.media_id ? { ...m, hearts: msg.hearts, glows: msg.glows } : m
          ));
        }
      } catch {
        // ignore malformed messages
      }
    };

    ws.onclose = () => {
      setIsConnected(false);
      // Reconnect after 2s with exponential backoff (capped at 30s)
      reconnectTimerRef.current = setTimeout(connectWebSocket, 2000);
    };

    ws.onerror = () => {
      ws.close();
    };
  }, [auraKey]);

  useEffect(() => {
    fetchInitial();
    connectWebSocket();
    return () => {
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, [fetchInitial, connectWebSocket]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-aura-purple to-aura-gold flex items-center justify-center mx-auto mb-4 animate-pulse">
            <Sparkles className="w-8 h-8 text-white" />
          </div>
          <p className="text-champagne font-serif text-xl">Connecting to AuraCast...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <p className="text-error text-xl mb-4">{error}</p>
          <Link to="/" className="text-champagne hover:underline">
            Return to Memouraa
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black overflow-hidden relative">
      {/* Ambient background */}
      <div className="fixed inset-0 pointer-events-none">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            background: `radial-gradient(ellipse at 30% 20%, ${aura?.primary_color || '#8E44AD'}40 0%, transparent 50%),
                        radial-gradient(ellipse at 70% 80%, ${aura?.accent_color || '#F39C12'}40 0%, transparent 50%)`
          }}
        />
      </div>

      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-50 p-4 sm:p-6 flex items-center justify-between gap-2">
        <button
          onClick={() => { if (window.history.length > 1) navigate(-1); else navigate(`/portal/${auraKey}`); }}
          className="text-text-muted hover:text-text-light transition-colors flex items-center gap-2"
        >
          <ArrowLeft className="w-5 h-5" />
          Exit
        </button>
        <div className="flex items-center gap-2 sm:gap-4">
          <Link
            to={`/auracast/${auraKey}/projection`}
            className="hidden sm:inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-champagne/10 hover:bg-champagne/20 text-champagne text-sm"
            title="Open slideshow (fullscreen)"
          >
            <Tv className="w-4 h-4" /> Slideshow
          </Link>
          <div className="flex items-center gap-2">
            {isConnected ? (
              <>
                <Wifi className="w-4 h-4 text-success" />
                <span className="text-success text-sm">Live</span>
              </>
            ) : (
              <>
                <WifiOff className="w-4 h-4 text-error" />
                <span className="text-error text-sm">Reconnecting...</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-aura-purple to-aura-gold flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <span className="font-serif text-text-light">AuraCast</span>
          </div>
        </div>
      </div>

      {/* Event Title */}
      <div className="fixed top-16 sm:top-20 left-0 right-0 text-center z-40 px-4">
        <h1 className="text-2xl sm:text-5xl font-serif font-medium text-text-light mb-2 truncate">
          {aura?.title}
        </h1>
        <p className="text-text-muted">
          {media.length} moments captured
        </p>
      </div>

      {/* New Item Notification */}
      <AnimatePresence>
        {newItem && (
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            className="fixed top-40 left-1/2 -translate-x-1/2 z-50"
          >
            <div className="aura-card rounded-full px-6 py-3 flex items-center gap-3 aura-glow">
              <span className="w-2 h-2 bg-champagne rounded-full animate-pulse" />
              <span className="text-text-light">New moment from {newItem.guest_name}</span>
              {newItem.mood && <MoodIcon mood={newItem.mood} className="w-5 h-5 text-champagne" />}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Media Display - Masonry Carousel */}
      <div className="pt-36 sm:pt-48 pb-16 px-4 sm:px-8 min-h-screen">
        {media.length === 0 ? (
          <div className="flex items-center justify-center h-[60vh]">
            <div className="text-center">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-aura-purple/20 to-aura-gold/20 flex items-center justify-center mx-auto mb-6">
                <Sparkles className="w-12 h-12 text-champagne/50" />
              </div>
              <p className="text-2xl font-serif text-text-muted">
                Waiting for moments...
              </p>
              <p className="text-text-muted mt-2">
                Share the portal link to start collecting
              </p>
            </div>
          </div>
        ) : (
          <div className="columns-1 sm:columns-2 md:columns-3 lg:columns-4 gap-4 space-y-4">
            {media.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ 
                  duration: 0.8,
                  delay: index * 0.1,
                  ease: "easeOut"
                }}
                className="break-inside-avoid relative group"
              >
                <div 
                  className="rounded-xl overflow-hidden"
                  style={{
                    boxShadow: `0 0 30px ${aura?.accent_color || '#D4AF37'}30`
                  }}
                >
                  {item.file_type === 'video' ? (
                    <video
                      src={`${API_URL}${item.url}`}
                      className="w-full"
                      autoPlay
                      muted
                      loop
                      playsInline
                    />
                  ) : (
                    <img
                      src={`${API_URL}${item.url}`}
                      alt=""
                      className="w-full"
                    />
                  )}
                  
                  {/* Overlay with info */}
                  <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="flex items-center justify-between">
                      <span className="text-text-light text-sm">{item.guest_name}</span>
                      <div className="flex items-center gap-2">
                        {item.mood && (
                          <MoodIcon mood={item.mood} className="w-5 h-5 text-champagne" />
                        )}
                        <span className="text-text-muted text-sm inline-flex items-center gap-1">
                          <Heart className="w-4 h-4" /> {item.hearts}
                          <Sparkles className="w-4 h-4 ml-2" /> {item.glows}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Footer branding */}
      <div className="fixed bottom-6 left-0 right-0 text-center">
        <p className="text-text-muted text-sm">
          Powered by <span className="text-champagne">Memouraa</span>
        </p>
      </div>
    </div>
  );
}
